/**
 * 커뮤니티 홈 — 활성 게시판 카드 그리드.
 *
 * 영구지침: docs/develop/community/board-cms.md §6.3, docs/develop/seo/ssr-spec.md §15.4
 */
import { Link } from 'react-router-dom'
import { MessageSquare, ChevronRight } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import { useCommunityBoards } from '@/features/community/publicApi'

function CommunityHomeSEO() {
  return (
    <SEO
      title="커뮤니티 - 요양 보호자 이야기·정보 나눔"
      description="요양시설 선택, 장기요양등급, 돌봄 경험까지 — 부모님을 모시는 보호자들이 정보와 이야기를 나누는 케어밸런스 커뮤니티입니다."
      url="/community"
    />
  )
}

export default function CommunityHomePage() {
  const { data: boards = [], isLoading } = useCommunityBoards()

  if (isLoading) {
    return (
      <>
        <CommunityHomeSEO />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    )
  }

  return (
    <>
      <CommunityHomeSEO />
      <main className="mx-auto w-full max-w-5xl px-4 py-8">
        <header className="mb-8">
          <h1 className="text-heading-lg font-bold text-cb-gray-900">커뮤니티</h1>
          <p className="mt-2 text-body-sm text-cb-gray-500">
            요양 정보와 경험을 나누는 보호자 공간입니다.
          </p>
        </header>

        {boards.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-cb-gray-200 bg-white p-12 text-center text-cb-gray-500">
            아직 개설된 게시판이 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {boards.map((b) => (
              <Link
                key={b.id}
                to={`/community/${b.slug}`}
                className="group flex items-center justify-between rounded-2xl border border-cb-gray-200 bg-white p-5 transition-all hover:border-cb-primary hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-cb-primary-light p-2.5 text-cb-primary">
                    <MessageSquare size={20} />
                  </div>
                  <div>
                    <h2 className="text-body-md font-semibold text-cb-gray-900">{b.name}</h2>
                    {b.description && (
                      <p className="mt-1 line-clamp-2 text-caption text-cb-gray-500">
                        {b.description}
                      </p>
                    )}
                    <div className="mt-2 flex gap-3 text-caption text-cb-gray-400">
                      <span>글 {b.postCount.toLocaleString()}</span>
                      {b.allowAnonymous && <span className="text-cb-primary">익명 가능</span>}
                      {b.enableAcceptedAnswer && <span>Q&A 채택</span>}
                    </div>
                  </div>
                </div>
                <ChevronRight
                  size={18}
                  className="text-cb-gray-300 transition-transform group-hover:translate-x-1 group-hover:text-cb-primary"
                />
              </Link>
            ))}
          </div>
        )}
      </main>
    </>
  )
}
